// «Мои задачи» над таблицей заявок: просрочено, сегодня, на неделе. Один
// запрос открытых задач, раскладка — здесь, чтобы колонки не пересекались.
import type { CrmTaskScope, CrmTaskWithLead } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { ChevronDown, ChevronUp, Circle, ListTodo } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "@/api";
import { useCrmTasks, useUpdateTask } from "@/api/hooks/use-application-crm";
import { Segmented } from "@/components/segmented";
import { cn } from "@/lib/utils";
import { errorText, formatDayTime, formatDue, overdueBy } from "./format";
import { TASK_ICON } from "./tasks-panel";
import { FieldError, Spinner } from "./ui";
const COLLAPSED_KEY = "osmix-admin.crm-tasks.collapsed";
const VISIBLE = 5;
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};
const dayStart = (offset: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return date.getTime();
};
type Group = {
  key: "overdue" | "today" | "week";
  title: string;
  empty: string;
  tasks: CrmTaskWithLead[];
};
const groupTasks = (tasks: CrmTaskWithLead[]): Group[] => {
  const tomorrow = dayStart(1);
  const weekEnd = dayStart(8);
  const due = (task: CrmTaskWithLead) => task.dueAt ? Date.parse(task.dueAt) : Number.POSITIVE_INFINITY;
  return [{
    key: "overdue",
    title: "Просрочено",
    empty: "Ничего не просрочено",
    tasks: tasks.filter(task => task.overdue)
  }, {
    key: "today",
    title: "Сегодня",
    empty: "На сегодня задач нет",
    tasks: tasks.filter(task => !task.overdue && due(task) < tomorrow)
  }, {
    key: "week",
    title: "На неделе",
    empty: "На неделю задач нет",
    tasks: tasks.filter(task => !task.overdue && due(task) >= tomorrow && due(task) < weekEnd)
  }];
};
export function MyTasksPanel() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<CrmTaskScope>("mine");
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [expanded, setExpanded] = useState<Group["key"] | null>(null);
  const {
    data,
    error,
    isLoading
  } = useCrmTasks(scope, "open");
  const update = useUpdateTask();
  const [actionError, setActionError] = useState<string | null>(null);

  // Без CRM на шлюзе (501/404) раздел работает как раньше — панели просто нет.
  if (error instanceof ApiError && (error.status === 501 || error.status === 404)) return null;
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      // приватный режим — просто не запоминаем
    }
  };
  const groups = groupTasks(data ?? []);
  const total = groups.reduce((sum, group) => sum + group.tasks.length, 0);
  const overdue = groups[0]?.tasks.length ?? 0;
  return <section className="rounded-xl border bg-card shadow-xs" data-testid="crm-my-tasks">
      <header className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        <button type="button" onClick={toggleCollapsed} aria-expanded={!collapsed} className="flex min-w-0 items-center gap-2 text-sm font-semibold">
          <ListTodo className="size-4 text-muted-foreground" />
          {scope === "mine" ? "Мои задачи" : "Задачи всех"}
          {total > 0 && <span className="text-xs font-normal tabular-nums text-muted-foreground">{total}</span>}
          {overdue > 0 && <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
              просрочено {overdue}
            </span>}
          {collapsed ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronUp className="size-4 text-muted-foreground" />}
        </button>
        {isLoading && <Spinner className="text-muted-foreground" />}
        <div className="ml-auto">
          <Segmented value={scope} onChange={setScope} options={[{
          value: "mine",
          label: "Мои"
        }, {
          value: "all",
          label: "Все"
        }]} />
        </div>
      </header>
      {!collapsed && <div className="grid gap-3 border-t px-4 py-3 md:grid-cols-3">
          {error && <FieldError>{errorText(error)}</FieldError>}
          {groups.map(group_0 => {
        const shown = expanded === group_0.key ? group_0.tasks : group_0.tasks.slice(0, VISIBLE);
        return <div key={group_0.key} className="flex min-w-0 flex-col gap-1">
                <p className={cn("flex items-center gap-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase", group_0.key === "overdue" && group_0.tasks.length > 0 && "text-destructive")}>
                  {group_0.title}
                  <span className="font-normal tabular-nums">{group_0.tasks.length}</span>
                </p>
                {group_0.tasks.length === 0 ? <p className="py-1 text-sm text-muted-foreground">{group_0.empty}</p> : <ul className="flex flex-col">
                    {shown.map(task => {
              const Icon = TASK_ICON[task.kind];
              return <li key={task.id} className="flex items-start gap-2 rounded-md px-1 py-1 hover:bg-muted/40">
                          <button type="button" aria-label={`Отметить выполненной: ${task.title}`} disabled={update.isPending} className="mt-0.5 shrink-0 text-muted-foreground hover:text-primary" onClick={() => {
                  setActionError(null);
                  update.mutate({
                    applicationId: task.application.id,
                    taskId: task.id,
                    patch: {
                      done: true
                    }
                  }, {
                    onError: e => setActionError(errorText(e))
                  });
                }}>
                            <Circle className="size-4" />
                          </button>
                          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(`/applications/${task.application.id}`)}>
                            <span className="block truncate text-sm">{task.title}</span>
                            <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                              <Icon className="size-3 shrink-0" />
                              <span className="truncate">{task.application.shopName}</span>
                              <span className="shrink-0">·</span>
                              <span className={cn("shrink-0", task.overdue && "text-destructive")}>
                                {task.overdue ? overdueBy(task.dueAt) : group_0.key === "week" ? formatDayTime(task.dueAt) : formatDue(task.dueAt)}
                              </span>
                            </span>
                            {scope === "all" && task.assignee && <span className="block truncate text-xs text-muted-foreground">{task.assignee.name}</span>}
                          </button>
                        </li>;
            })}
                  </ul>}
                {group_0.tasks.length > VISIBLE && <Button size="xs" variant="ghost" className="self-start" onClick={() => setExpanded(expanded === group_0.key ? null : group_0.key)}>
                    {expanded === group_0.key ? "Свернуть" : `Ещё ${group_0.tasks.length - VISIBLE}`}
                  </Button>}
              </div>;
      })}
          {actionError && <FieldError>{actionError}</FieldError>}
        </div>}
    </section>;
}